import { useMeta } from '../data/hooks'
import { formatDate } from '../detail/format'
import { useLocale } from '../detail/useLocale'
import { useStrings } from '../i18n'
import './about.css'
import { useOverlayStore } from './overlayStore'
import { newestEipaTimestamp, SOURCE_LINKS } from './sources'

/**
 * The always-visible attribution line, and the host for the two full-screen
 * surfaces it opens.
 *
 * This is a licence obligation rather than a footer nicety: ODbL and CC BY both
 * require the credit to be visible to the end user of the map, not buried on an
 * about page, and the Urząd Dozoru Technicznego's reuse conditions require the
 * source and the date of the data to be stated. So the footer names all four
 * sources, links each one, and carries the publish date read live from
 * `meta.json` - never a build-time constant, which would go stale the first
 * time the pipeline ran without a redeploy.
 */
export const AttributionFooter = () => {
  const strings = useStrings()
  const locale = useLocale()
  const open = useOverlayStore((state) => state.open)
  const { resource } = useMeta()

  const updated =
    resource.status === 'ready' ? newestEipaTimestamp(resource.value.sources.eipa) : null

  return (
    <div className="ev-attribution">
      {/* Every licence is named here rather than only on the about page: CC BY
            and ODbL both require the credit to reach the end user of the map,
            and somebody who never opens "O projekcie" is still an end user. */}
      <span className="ev-attribution__group">
        <span className="ev-attribution__label">{strings.footer.data}:</span>{' '}
        <a href={SOURCE_LINKS.eipa} target="_blank" rel="noreferrer noopener">
          EIPA / Urząd Dozoru Technicznego
        </a>
        {' · '}
        <a href={SOURCE_LINKS.ocm} target="_blank" rel="noreferrer noopener">
          Open Charge Map
        </a>{' '}
        <a href={SOURCE_LINKS.ccBy40} target="_blank" rel="noreferrer noopener">
          CC BY 4.0
        </a>
        {' / '}
        <a href={SOURCE_LINKS.odbl} target="_blank" rel="noreferrer noopener">
          ODbL
        </a>
      </span>

      <span className="ev-attribution__group">
        <span className="ev-attribution__label">{strings.footer.basemap}:</span>{' '}
        <a href={SOURCE_LINKS.openFreeMap} target="_blank" rel="noreferrer noopener">
          OpenFreeMap
        </a>
        {' · © '}
        <a href={SOURCE_LINKS.openStreetMap} target="_blank" rel="noreferrer noopener">
          OpenStreetMap
        </a>{' '}
        <a href={SOURCE_LINKS.odbl} target="_blank" rel="noreferrer noopener">
          ODbL
        </a>
        {' · '}
        <a href={SOURCE_LINKS.maplibre} target="_blank" rel="noreferrer noopener">
          MapLibre
        </a>{' '}
        <a href={SOURCE_LINKS.maplibreLicence} target="_blank" rel="noreferrer noopener">
          BSD-3-Clause
        </a>
      </span>

      <span className="ev-attribution__group ev-attribution__actions">
        {updated !== null && (
          <span className="ev-attribution__updated">
            {strings.footer.updated(formatDate(updated, locale))}
          </span>
        )}
        <button
          type="button"
          className="ev-button ev-button--quiet"
          onClick={() => open('methodology')}
        >
          {strings.footer.methodology}
        </button>
        <button type="button" className="ev-button ev-button--quiet" onClick={() => open('about')}>
          {strings.footer.about}
        </button>
      </span>
    </div>
  )
}
